import { showHttpError, showSuccess } from '@calendly/alert'
import { parseQuery } from '@calendly/http-tools'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { isNil, merge, omitBy } from 'lodash-es'

import { setGetParams } from 'actions/core/navigation'
import { upgradeSubscription } from 'components/billing/use_upgrade_subscription'
import { fetchCurrentSubscription } from 'features/billing'
import { fetchStatistics } from 'features/organization_statistics'

import {
  batchResendInvitations as batchResendUsersInvitations,
  deleteInvitations as deleteUsersInvitations,
  getInvitations,
  resendInvitation as resendUserInvitation
} from '../api'
import {
  excludedIdsSelector,
  filterTermSelector,
  isEndlessScrollEnabledSelector,
  paginationSelector,
  removeInvitationsPopupMembersSelector,
  selectAllStatusSelector,
  selectedIdsSelector,
  sliceSelector,
  sortFieldSelector,
  sortOrderSelector
} from './selectors'

export const fetchInvitations = createAsyncThunk(
  'usersPage/fetchInvitations',
  async ({ resetInvitations = false } = {}, { getState, dispatch, rejectWithValue }) => {
    const state = getState()
    const page = paginationSelector(state).currentPage
    const filter = filterTermSelector(state)
    const sortField = sortFieldSelector(state)
    const sortOrder = sortOrderSelector(state)
    const [success, response] = await getInvitations(
      omitBy({ page, filter_term: filter, sort_field: sortField, sort_order: sortOrder }, isNil)
    )
    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    return merge(response, { resetInvitations })
  }
)

const initialState = {
  invitations: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  },
  filter: '',
  sortField: 'email',
  sortOrder: 'asc',
  isLoading: true,
  selectedInvitations: [],
  selectAll: false,
  excludedIds: [],
  removeInvitationsPopup: {
    members: [],
    isOpen: false
  },
  isEndlessScrollEnabled: false
}

const slice = createSlice({
  name: 'usersPage/pending',
  initialState,
  reducers: {
    resetSelection: (state, { payload: invitations }) => {
      const { selectedInvitations } = state
      const ids = invitations.map(({ id }) => id)
      state.selectedInvitations = selectedInvitations.filter(({ id }) => !ids.includes(id))
    },
    toggleSelection: (state, { payload: invitation }) => {
      if (state.selectedInvitations.some(({ id }) => id === invitation.id)) {
        state.selectedInvitations = state.selectedInvitations.filter(({ id }) => id !== invitation.id)
      } else {
        state.selectedInvitations.push(invitation)
      }
    },
    setSelectAll: (state, { payload }) => {
      state.selectAll = payload
    },
    toggleExcludedId: (state, { payload: invitation }) => {
      if (state.excludedIds.includes(invitation.id)) {
        state.excludedIds = state.excludedIds.filter((id) => id !== invitation.id)
      } else {
        state.excludedIds.push(invitation.id)
      }
    },
    clearExcludedIds: (state) => {
      state.excludedIds = []
    },
    addSelectedInvitations: (state, { payload: invitations }) => {
      state.selectedInvitations.push(...invitations)
    },
    clearSelection: (state) => {
      state.selectedInvitations = []
    },
    setPage: (state, { payload }) => {
      state.pagination.currentPage = payload
    },
    setFilter: (state, { payload }) => {
      state.filter = payload
    },
    setEndlessScroll: (state, { payload }) => {
      state.isEndlessScrollEnabled = payload
    },
    setIsLoading: (state, { payload }) => {
      state.isLoading = payload
    },
    openBatchRemoveInvitationsPopup(state) {
      state.removeInvitationsPopup.isOpen = true
      state.removeInvitationsPopup.members = state.selectedInvitations
    },
    openRemoveInvitationsPopup(state, action) {
      state.removeInvitationsPopup.isOpen = true
      state.removeInvitationsPopup.members = [action.payload]
    },
    closeRemoveInvitationsPopup(state) {
      state.removeInvitationsPopup.isOpen = false
      state.removeInvitationsPopup.members = []
    },
    setSortOrder(state, { payload }) {
      state.sortOrder = payload
    },
    setSortField(state, { payload }) {
      state.sortField = payload
    },
    sortBy(state, { payload }) {
      if (state.sortField === payload) {
        state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        state.sortOrder = 'asc'
      }

      state.sortField = payload
    },
    resetState() {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInvitations.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(fetchInvitations.fulfilled, (state, { payload }) => {
      if (state.isEndlessScrollEnabled && !payload.resetInvitations) {
        state.invitations = [...state.invitations, ...payload.results]
        if (state.selectAll) {
          state.selectedInvitations = [...state.selectedInvitations, ...payload.results]
        }
      } else {
        state.invitations = payload.results
      }

      state.pagination = {
        currentPage: payload.pagination.current_page,
        totalPages: payload.pagination.total_pages,
        totalCount: payload.pagination.total_count,
        pageSize: payload.pagination.page_size
      }

      state.isLoading = false
    })
    builder.addCase(fetchInvitations.rejected, (state) => {
      state.isLoading = false
    })
  }
})

export const togglePageSelection = (filterFunction) => (dispatch, getState) => {
  const state = getState()
  const { invitations: selectableEntities } = sliceSelector(state)
  const selectedIds = selectedIdsSelector(state)

  let remainingInvitations = selectableEntities.filter(({ id }) => !selectedIds.includes(id))

  if (filterFunction) {
    remainingInvitations = remainingInvitations.filter(filterFunction)
  }

  if (remainingInvitations.length) {
    dispatch(slice.actions.addSelectedInvitations(remainingInvitations))
  } else {
    dispatch(slice.actions.resetSelection(selectableEntities))
  }
}

export const removeInvitations = () => async (dispatch, getState) => {
  const state = getState()
  const ids = removeInvitationsPopupMembersSelector(state).map((member) => member.id)
  const [success, response] = await deleteUsersInvitations(ids)

  if (!success) {
    dispatch(showHttpError(response))
    return success
  }

  dispatch(slice.actions.closeRemoveInvitationsPopup())
  dispatch(slice.actions.clearSelection())
  dispatch(fetchInvitations())
  dispatch(fetchStatistics())
  dispatch(fetchCurrentSubscription())

  return success
}

export const resendInvitation = (invitationId) => async (dispatch, getState) => {
  const isEndlessScrollEnabled = isEndlessScrollEnabledSelector(getState())

  const [success, response] = await resendUserInvitation(invitationId)

  if (success) {
    dispatch(showSuccess(response.message, {}))
  } else if (response.type === 'quantity_overflow') {
    const {
      invitations_quantity: invitationsQuantity,
      min_quantity: minQuantity
    } = response.payload

    upgradeSubscription({
      dispatch,
      featureKey: 'organization_invitation',
      skipPreface: true,
      minQuantity,
      invitationsQuantity
    })
  } else {
    dispatch(showHttpError(response))
  }

  if (isEndlessScrollEnabled) dispatch(slice.actions.setPage(1))
  dispatch(fetchInvitations({ resetInvitations: true }))

  return success
}

export const batchResendInvitations = () => async (dispatch, getState) => {
  const state = getState()
  const selectedIds = selectedIdsSelector(state)
  const isEndlessScrollEnabled = isEndlessScrollEnabledSelector(state)
  const selectAllStatus = selectAllStatusSelector(state)
  const excludedIds = excludedIdsSelector(state)

  const [success, response] = await batchResendUsersInvitations(selectedIds, selectAllStatus, excludedIds)

  if (success) {
    dispatch(showSuccess(response.message, {}))
    dispatch(slice.actions.setSelectAll(false))
    dispatch(slice.actions.clearSelection())
    dispatch(slice.actions.clearExcludedIds())
  } else if (response.type === 'quantity_overflow') {
    const {
      invitations_quantity: invitationsQuantity,
      min_quantity: minQuantity
    } = response.payload || {}

    upgradeSubscription({
      dispatch,
      featureKey: 'organization_invitation',
      skipPreface: true,
      minQuantity,
      invitationsQuantity
    })
  } else {
    dispatch(showHttpError(response))
  }

  if (isEndlessScrollEnabled) dispatch(slice.actions.setPage(1))
  dispatch(fetchInvitations({ resetInvitations: true }))

  return success
}

export const updatePage = ({ page }) => (dispatch, getState) => {
  const isEndlessScrollEnabled = isEndlessScrollEnabledSelector(getState())

  if (isEndlessScrollEnabled) {
    dispatch(slice.actions.setIsLoading(true))
  } else {
    dispatch(setGetParams({ page }))
  }

  dispatch(slice.actions.setPage(page))
  dispatch(fetchInvitations())
}

export const initPage = () => async (dispatch) => {
  const params = parseQuery(window.location.search)
  const page = params.page ? parseInt(params.page, 10) : 1
  dispatch(slice.actions.setPage(page))

  if (params.sort_field) dispatch(slice.actions.setSortField(params.sort_field))
  if (params.sort_order) dispatch(slice.actions.setSortOrder(params.sort_order))

  const { error, payload: results } = await dispatch(fetchInvitations())

  if (error) return

  if (results.pagination.is_out_of_range) {
    await dispatch(updatePage({ page: 1 }))
  }
}

export const search = (query) => (dispatch) => {
  dispatch(slice.actions.setFilter(query))
  dispatch(slice.actions.setPage(1))
  dispatch(setGetParams({ page: null }))
  dispatch(fetchInvitations())
}

export const sortInvitations = (field) => async (dispatch, getState) => {
  dispatch(slice.actions.sortBy(field))
  dispatch(slice.actions.setPage(1))
  const state = getState()
  const sortOrder = sortOrderSelector(state)
  const sortField = sortFieldSelector(state)
  dispatch(setGetParams({ sort_field: sortField, sort_order: sortOrder, page: null }))
  await dispatch(fetchInvitations())
}

export const enableEndlessScroll = () => (dispatch) => {
  dispatch(slice.actions.setEndlessScroll(true))
}

export const { reducer, actions: {
  setIsLoading,
  toggleSelection,
  openBatchRemoveInvitationsPopup,
  openRemoveInvitationsPopup,
  closeRemoveInvitationsPopup,
  clearSelection,
  resetState,
  setSelectAll,
  toggleExcludedId,
  clearExcludedIds
} } = slice
